import { Menu, MenuItem, MenuItemLabel, MenuSeparator } from '@/components/gluestack/menu';
import { Text } from '@/components/gluestack/text';
import { Avatar, AvatarImage } from '@/components/gluestack/avatar';
import React, { JSX } from 'react';
import { TouchableOpacity, Platform } from 'react-native';
import { router } from 'expo-router';
import { useAuth } from '@/components/providers/AuthProvider';
import { useSignalR } from '@/components/providers/SignalRProvider';
import {
  Settings01Icon,
  UserAccountIcon,
  Logout01Icon,
  StartUp02Icon,
  HelpSquareIcon,
  ShieldUserIcon,
  City03Icon,
  ZapIcon,
} from '@hugeicons-pro/core-solid-rounded';
import { SUPPORT_URLS } from '@/constants/urls';
import { MenuItemIcon } from '@/components/ui/MenuItemIcon';
import { useIsLargeScreen } from '~/hooks/useIsLargeScreen';
import { useOpenWebPage } from '@/hooks/useOpenWebPage';
import { HStack } from '../gluestack/hstack';
import { useTranslation } from 'react-i18next'; // Import useTranslation

const UserAvatar = ({ imageUrl }: { imageUrl?: string }): JSX.Element => {
  return (
    <Avatar size="sm">
      <AvatarImage
        key={imageUrl}
        source={{
          uri: imageUrl,
        }}
      />
    </Avatar>
  );
};

export function UserProfileMenu() {
  const { signOut, accountStatus, currentCommunity, getCredentials } = useAuth();
  const { disconnect } = useSignalR();
  const isLargeScreen = useIsLargeScreen();
  const openWebPage = useOpenWebPage(); // Get theme-aware web page opener
  const { t } = useTranslation(); // Get translation function

  // Fallback to stored credentials if accountStatus is not available (e.g., due to 403 error)
  const credentials = currentCommunity ? getCredentials(currentCommunity) : null;
  const displayUsername = accountStatus?.UserName || credentials?.UserName || 'User';

  const handleNavigateWithClose = (path: any) => {
    // Small delay to ensure menu closes before navigation
    setTimeout(() => {
      router.navigate(path);
    }, 50);
  };

  const handleWebPageWithClose = (url: string) => {
    // Small delay to ensure menu closes before opening web page
    setTimeout(() => {
      openWebPage(url);
    }, 50);
  };

  const handleBrowseCommunities = () => {
    if (Platform.OS === 'web') {
      window.open('/communities', '_blank');
      return;
    }
    // Push the directory on top while staying signed in: backing out (or
    // re-picking the current community) is then a plain pop with every tab
    // stack intact. The soft sign-out previously used here destroyed the
    // (app) tree and cleared all query caches just for browsing, so a
    // "never mind" cost a full app remount.
    router.push({ pathname: '/communities', params: { from: 'profileMenu' } });
  };

  const handleSignOut = async () => {
    try {
      await disconnect();
    } catch (error) {
      console.error('Error disconnecting from SignalR:', error);
    }
    signOut();
  };

  return (
    <Menu
      placement="bottom right"
      offset={6}
      crossOffset={isLargeScreen ? 0 : 4}
      trigger={({ ...triggerProps }) => {
        return (
          <TouchableOpacity activeOpacity={0.7} {...triggerProps}>
            <HStack
              space="sm"
              className="items-center rounded-full bg-secondary-600 p-1 dark:bg-secondary-300">
              <Text className="ml-3 text-xs font-bold uppercase text-white">
                {displayUsername}
              </Text>
              <UserAvatar imageUrl={accountStatus?.IconUrl} />
            </HStack>
          </TouchableOpacity>
        );
      }}>
      <MenuItem
        key="view-profile"
        textValue={t('userMenu.viewProfile')}
        onPress={() => handleNavigateWithClose(`/@/${displayUsername}`)}>
        <MenuItemIcon icon={UserAccountIcon} />
        <MenuItemLabel>{t('userMenu.viewProfile')}</MenuItemLabel>
      </MenuItem>

      <MenuSeparator />
      <MenuItem
        key="settings"
        textValue={t('userMenu.settings')}
        onPress={() => handleNavigateWithClose('/account/settings')}>
        <MenuItemIcon icon={Settings01Icon} />
        <MenuItemLabel>{t('userMenu.settings')}</MenuItemLabel>
      </MenuItem>
      <MenuItem
        key="plans"
        textValue={t('userMenu.plans')}
        onPress={() => handleNavigateWithClose('/account/plans')}>
        <MenuItemIcon icon={StartUp02Icon} />
        <MenuItemLabel>{t('userMenu.plans')}</MenuItemLabel>
      </MenuItem>
      <MenuSeparator />
      <MenuItem
        key="help"
        textValue={t('userMenu.help')}
        onPress={() =>
          handleWebPageWithClose(SUPPORT_URLS.atlassian.support.helpCenter)
        }>
        <MenuItemIcon icon={HelpSquareIcon} />
        <MenuItemLabel>{t('userMenu.help')}</MenuItemLabel>
      </MenuItem>
      <MenuItem
        key="feedback"
        textValue={t('userMenu.feedback')}
        onPress={() =>
          handleWebPageWithClose(SUPPORT_URLS.atlassian.support.newPlatformFeedback)
        }>
        <MenuItemIcon icon={ZapIcon} />
        <MenuItemLabel>{t('userMenu.feedback')}</MenuItemLabel>
      </MenuItem>
      <MenuItem
        key="rules"
        textValue={t('userMenu.rules')}
        onPress={() =>
          handleWebPageWithClose(SUPPORT_URLS.atlassian.legal.communityRules)
        }>
        <MenuItemIcon icon={ShieldUserIcon} />
        <MenuItemLabel>{t('userMenu.rules')}</MenuItemLabel>
      </MenuItem>

      <MenuSeparator />
      <MenuItem
        key="browse-communities"
        textValue={t('userMenu.browseCommunities')}
        onPress={handleBrowseCommunities}>
        <MenuItemIcon icon={City03Icon} />
        <MenuItemLabel>{t('userMenu.browseCommunities')}</MenuItemLabel>
      </MenuItem>
      <MenuItem key="signout" textValue={t('userMenu.signOut')} onPress={handleSignOut}>
        <MenuItemIcon icon={Logout01Icon} />
        <MenuItemLabel>{t('userMenu.signOut')}</MenuItemLabel>
      </MenuItem>
    </Menu>
  );
}
